import React, { useState, useEffect } from 'react';
import { Mail, Phone, KeySquare, User } from 'lucide-react';

const indianMobileRegex = /^(?:\+91[\-\s]?)?[6-9]\d{9}$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddUserForm = ({ initialFormData, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState(initialFormData);
    const [errors, setErrors] = useState({});

    const validateForm = (data) => {
        const newErrors = {};

        if (!data.name || data.name.trim() === '') {
            newErrors.name = 'Name is required.';
        }

        if (!data.email || data.email.trim() === '') {
            newErrors.email = 'Email is required.';
        } else if (!emailRegex.test(data.email)) {
            newErrors.email = 'Please enter a valid email address.';
        }

        if (!data.mobile || data.mobile.trim() === '') {
            newErrors.mobile = 'Mobile number is required.';
        } else if (!indianMobileRegex.test(data.mobile)) {
            newErrors.mobile = 'Please enter a valid Indian mobile number.';
        }

        if (!data.password || data.password.trim() === '') {
            newErrors.password = 'Password is required.';
        }

        return newErrors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });

        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const validationErrors = validateForm(formData);
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length === 0) {
            onSubmit(formData);
        }
    };

    const getInputClasses = (fieldName) => {
        const baseClasses = "w-full px-4 py-2 border rounded-r-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
        const errorClasses = "border-red-500 focus:ring-red-500 focus:border-red-500";
        const normalClasses = "border-gray-300";

        return `${baseClasses} ${errors[fieldName] ? errorClasses : normalClasses}`;
    };

    const getIconContainerClasses = (fieldName) => {
        const baseClasses = "inline-flex items-center px-3 rounded-l-md border border-r-0 bg-gray-50 text-gray-500";
        const errorClasses = "border-red-500";
        const normalClasses = "border-gray-300";
        return `${baseClasses} ${errors[fieldName] ? errorClasses : normalClasses}`;
    }


    return (
        <div className="bg-white rounded-lg shadow-sm">
            <div className="space-y-6 bg-white p-6 rounded-lg">
                <form onSubmit={handleSubmit} noValidate>
                    <div className="space-y-6">
                        {/* Name Field */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                                Name {errors.name && <span className="text-red-600">*</span>}
                            </label>
                            <div className="flex">
                                <span className={getIconContainerClasses('name')}>
                                    <User className="h-4 w-4" />
                                </span>
                                <input
                                    type="text"
                                    name="name"
                                    id="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className={getInputClasses('name')}
                                    aria-invalid={errors.name ? "true" : "false"}
                                    aria-describedby={errors.name ? "name-error" : undefined}
                                />
                            </div>
                            {errors.name && <p id="name-error" className="mt-1 text-xs text-red-600">{errors.name}</p>}
                        </div>

                        {/* Email Field */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email {errors.email && <span className="text-red-600">*</span>}
                            </label>
                            <div className="flex">
                                <span className={getIconContainerClasses('email')}>
                                    <Mail className="h-4 w-4" />
                                </span>
                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className={getInputClasses('email')}
                                    aria-invalid={errors.email ? "true" : "false"}
                                    aria-describedby={errors.email ? "email-error" : undefined}
                                />
                            </div>
                            {errors.email && <p id="email-error" className="mt-1 text-xs text-red-600">{errors.email}</p>}
                        </div>

                        {/* Mobile Field */}
                        <div>
                            <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-1">
                                Mobile {errors.mobile && <span className="text-red-600">*</span>}
                            </label>
                            <div className="flex">
                                <span className={getIconContainerClasses('mobile')}>
                                    <Phone className="h-4 w-4" />
                                </span>
                                <input
                                    type="tel"
                                    name="mobile"
                                    id="mobile"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    required
                                    className={getInputClasses('mobile')}
                                    aria-invalid={errors.mobile ? "true" : "false"}
                                    aria-describedby={errors.mobile ? "mobile-error" : undefined}
                                />
                            </div>
                            {errors.mobile && <p id="mobile-error" className="mt-1 text-xs text-red-600">{errors.mobile}</p>}
                        </div>

                        {/* Password Field */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                Password {errors.password && <span className="text-red-600">*</span>}
                            </label>
                            <div className="flex">
                                <span className={getIconContainerClasses('password')}>
                                    <KeySquare className="h-4 w-4" />
                                </span>
                                <input
                                    type="password"
                                    name="password"
                                    id="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    required
                                    className={getInputClasses('password')}
                                    aria-invalid={errors.password ? "true" : "false"}
                                    aria-describedby={errors.password ? "password-error" : undefined}
                                />
                            </div>
                            {errors.password && <p id="password-error" className="mt-1 text-xs text-red-600">{errors.password}</p>}
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex justify-end space-x-4 mt-8">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-6 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            Add User
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

AddUserForm.defaultProps = {
    initialFormData: {
        name: '',
        email: '',
        mobile: '',
        password: '',
    },
};


export default AddUserForm;